import { SessionStateService } from '@abp/ng.core';
import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PhraseTypeDto } from 'src/core/models/conversation/phrase-type/phrase-type.dto';
import { RedactionExceptionListDto } from 'src/core/models/conversation/phrase-type/redaction-exception-list.dto';

export interface LanguageOption {
  code: string;
  label: string;
}

@Component({
  selector: 'ca-redaction-exception-phrases',
  templateUrl: './redaction-exception-phrases.component.html',
  styleUrls: ['./redaction-exception-phrases.component.scss'],
  standalone: false,
})
export class RedactionExceptionPhrasesComponent {
  @ViewChild('exceptionPhrasesModal') exceptionPhrasesModal: any;
  @Output() phraseSaved = new EventEmitter<PhraseTypeDto>();

  phraseType: PhraseTypeDto | null = null;
  languagesDataSource: LanguageOption[] = [];
  exceptionForm: FormGroup;

  get languagesControl(): FormControl {
    return this.exceptionForm.get('languages') as FormControl;
  }

  get phrasesArray(): FormArray {
    return this.exceptionForm.get('phrases') as FormArray;
  }

  getPhraseGroup(index: number): FormGroup {
    return this.phrasesArray.at(index) as FormGroup;
  }

  getPhraseItems(groupIndex: number): FormArray {
    return this.getPhraseGroup(groupIndex).get('items') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private sessionStateService: SessionStateService,
  ) {
    this.exceptionForm = this.fb.group({
      languages: [[]],
      phrases: this.fb.array([]),
    });
  }

  openModal(phraseType: PhraseTypeDto): void {
    if (!phraseType) return;

    this.phraseType = phraseType;
    this.exceptionForm = this.fb.group({
      languages: [[]],
      phrases: this.fb.array([]),
    });

    const currentLang = this.sessionStateService.getLanguage();
    const displayNames = new Intl.DisplayNames([currentLang], { type: 'language' });
    this.languagesDataSource = phraseType.supportedLanguages
      .split(',')
      .map(l => l.trim())
      .filter(l => l.length > 0)
      .map(l => ({ code: l, label: displayNames.of(l) || l }));

    const phrasesArray = this.exceptionForm.get('phrases') as FormArray;
    const preSelectedLanguages: LanguageOption[] = [];

    (phraseType.exceptions || []).forEach(ex => {
      const lang = this.languagesDataSource.find(l => l.code === ex.language);
      if (!lang || !ex.phrases?.length) return;

      preSelectedLanguages.push(lang);
      phrasesArray.push(
        this.fb.group({
          languageCode: [lang.code],
          languageLabel: [lang.label],
          newPhrase: [''],
          items: this.fb.array(ex.phrases.map(p => this.fb.control(p))),
        }),
      );
    });

    this.exceptionForm.get('languages').setValue(preSelectedLanguages);

    this.modalService.open(this.exceptionPhrasesModal, {
      backdrop: 'static',
      keyboard: false,
      scrollable: true,
      size: 'lg',
      windowClass: 'redaction-exception-phrases-modal',
    });
  }

  onLanguagesChange(selected: LanguageOption[]): void {
    const selectedCodes = (selected || []).map(l => l.code);
    const currentCodes = this.phrasesArray.controls.map(g => g.get('languageCode').value);

    // Remove groups for deselected languages
    for (let i = this.phrasesArray.length - 1; i >= 0; i--) {
      if (!selectedCodes.includes(currentCodes[i])) {
        this.phrasesArray.removeAt(i);
      }
    }

    // Add groups for newly selected languages (preserving order)
    const remainingCodes = this.phrasesArray.controls.map(g => g.get('languageCode').value);
    selectedCodes.forEach(code => {
      if (!remainingCodes.includes(code)) {
        const lang = this.languagesDataSource.find(l => l.code === code);
        this.phrasesArray.push(
          this.fb.group({
            languageCode: [code],
            languageLabel: [lang?.label || code],
            newPhrase: [''],
            items: this.fb.array([]),
          }),
        );
      }
    });
  }

  addPhrase(groupIndex: number): void {
    const group = this.getPhraseGroup(groupIndex);
    const val: string = (group.get('newPhrase').value || '').trim();
    if (!val) return;

    this.addExceptionPhrase(groupIndex, val);
    group.get('newPhrase').setValue('');
  }

  addExceptionPhrase(groupIndex: number, val: string): void {
    const items = this.getPhraseItems(groupIndex);
    const alreadyExists = items.controls.some(c => c.value === val);
    if (!alreadyExists) {
      items.push(this.fb.control(val));
    }
  }

  onPhrasesPasted(groupIndex: number, items: string[]): void {
    items.forEach(item => this.addExceptionPhrase(groupIndex, item));
    this.getPhraseGroup(groupIndex).get('newPhrase').setValue('');
  }

  onNewPhraseKeydown(event: KeyboardEvent, groupIndex: number): void {
    if (event.key === 'Enter') {
      this.addPhrase(groupIndex);
      event.preventDefault();
    }
  }

  removePhrase(groupIndex: number, itemIndex: number): void {
    this.getPhraseItems(groupIndex).removeAt(itemIndex);
  }

  onSet(): void {
    const exceptions: RedactionExceptionListDto[] = this.phrasesArray.controls.map(g => ({
      language: g.get('languageCode').value as string,
      phrases: (g.get('items') as FormArray).controls.map(c => c.value as string),
    }));

    let deletedLanguageExceptions = this.languagesDataSource
      .filter(l => !exceptions.map(e => e.language).includes(l.code))
      .map(l => ({ language: l.code, phrases: [] }));

    this.phraseType = {
      ...this.phraseType,
      exceptions: exceptions.concat(deletedLanguageExceptions),
    };
    this.phraseSaved.emit(this.phraseType);
    this.closeModal();
  }

  closeModal(): void {
    this.modalService.dismissAll();
  }
}
